import styled from 'styled-components';
import BackButton from '../utils/back_button';
import { blog1 } from "../../../assets/images";
import { useNavigate } from 'react-router-dom';
import OtherBlogs from '../our_blogs/OtherBlogs';
import CTASection from "../../landing/landing_home/empower";

export default function Blog1() {

  return (
    <>
     <Fullpage>
      <PageBackground>
        <BlogContainer>
          <BlogWrapper>
            <BlogHeader
              title="From Overwhelmed to Empowered: How AI is Transforming Professors' Lives Across Campus"
              date="July 17, 2025"
              author="posted by campus insights"
              
            />
            <BlogContent />
          </BlogWrapper>
        </BlogContainer>
        <OtherBlogs currentBlogId={1} />
     </PageBackground>
     
     </Fullpage>
     <CTASection />
    
    </>
  );
};

const BlogHeader = ({ title, date, author}) => {
   const navigate = useNavigate();
  const handleBackClick = () => {
     window.scrollTo(0, 0);
     navigate("/blog")
  };
  return (
    <HeaderContainer>
      <HeaderContent>
        <BackButtonWrapper>
          <BackButton onClick={ handleBackClick} />
        </BackButtonWrapper>
        <MainTitle>{title}</MainTitle>
        <MetadataContainer>
          <MetadataWrapper>
            <MetadataContent>
              <DateText>{date}</DateText>
              <AuthorText>{author}</AuthorText>
            </MetadataContent>
          </MetadataWrapper>
        </MetadataContainer>
      </HeaderContent>
      <HeroImage src={blog1}  />
      <IntroText>
        The academic world is undergoing a profound transformation as artificial intelligence becomes embedded in the fabric of higher education. For professors balancing the demands of research, teaching, administration, and service, AI offers more than just new tools—it provides a powerful ally to streamline daily tasks, foster innovation, and reclaim time for what truly matters: advancing knowledge and inspiring the next generation.
      </IntroText>
    </HeaderContainer>
  );
};

const BlogContent = () => {
  return (
    <ContentContainer>
      <BlogSection title="The Challenge of Academic Overload">
        <ContentText>Ask any professor about their week, and you'll hear a familiar story:</ContentText>
        <ContentText>Preparing and delivering lectures</ContentText>
        <ContentText>Grading assignments and providing feedback</ContentText>
        <ContentText>Attending committee meetings and managing documentation</ContentText>
        <ContentText>Pursuing research, publishing, and grant applications</ContentText>
        <ContentText>Responding to a flood of student emails and requests for office hours</ContentText>
        <ContentText>
          This relentless pace often leaves little space for creative teaching or meaningful mentorship—the heart of academic life. What's needed isn't another complex system, but a seamless partner that integrates into the daily workflow. This is where AI is making a difference.
        </ContentText>
      </BlogSection>

      <BlogSection title="Revolutionizing Communication in the Classroom" ismainsection="true">
        <SubSection title="AI-Powered Email Management">
          AI-driven email assistants are reshaping how professors handle the constant influx of student inquiries. These systems can automatically sort messages, draft responses to common questions, and flag urgent communications. For routine queries about assignments, deadlines, or course policies, AI ensures consistent, helpful replies that retain the professor's personal touch.
        </SubSection>

        <SubSection title="Automated Scheduling and Office Hours">
          AI scheduling tools can coordinate office hours, research meetings, and student consultations with minimal effort. By analyzing availability and preferences, these systems suggest optimal meeting times and handle rescheduling conflicts. Intelligent booking interfaces also gather preliminary information from students, helping professors prepare for each meeting.
        </SubSection>

        <SubSection title="Real-Time Lecture Enhancement">
          AI transcription services capture lectures as they happen, producing searchable transcripts for both professors and students. These transcripts can be automatically edited, translated, and integrated into learning management systems, allowing professors to focus on teaching while AI manages the documentation.
        </SubSection>
      </BlogSection>

      <BlogSection title="Streamlining Repetitive Academic Tasks" ismainsection="true">
        <SubSection title="Automated Grading and Feedback">
          AI can handle the initial grading of objective assessments, freeing professors to concentrate on providing deeper, personalized feedback on complex assignments. For essays, AI can highlight common issues, suggest areas for student improvement, and generate feedback templates for customization.
        </SubSection>

        <SubSection title="Research Literature Management">
          AI research assistants monitor academic databases, summarize key findings, and identify collaboration opportunities. By analyzing citation patterns and tracking emerging trends, these tools keep professors informed about the latest developments in their fields.
        </SubSection>

        <SubSection title="Administrative Documentation">
          From syllabi to committee reports, AI can draft and format academic documents, ensuring consistency and compliance with institutional policies. It can also suggest improvements based on best practices from similar documents.
        </SubSection>
      </BlogSection>

      <BlogSection title="Enhancing Teaching Innovation" ismainsection="true">
        <SubSection
          title="Personalized Learning Pathways"
          image="https://api.builder.io/api/v1/image/assets/TEMP/8d340e67ddc3e85ce43309e8f787132201811560?width=1526"
          imageAlt="AI-powered learning analytics visualization"
        >
          AI analytics can identify students who need extra support or alternative learning strategies, enabling professors to intervene early and adapt their teaching to diverse learning styles.
        </SubSection>

        <SubSection title="Dynamic Content Creation">
          AI assists in generating educational materials—practice problems, quizzes, simulations, and visual aids—that adapt in difficulty based on class performance. It can also create multiple versions of assignments to uphold academic integrity.
        </SubSection>

        <SubSection title="Intelligent Tutoring Support">
          AI-powered teaching assistants provide round-the-clock support, answering basic questions and directing students to resources. This reduces interruptions and allows professors to focus on research and mentorship.
        </SubSection>
      </BlogSection>

      <BlogSection title="Campus-Wide Collaboration and Service" ismainsection="true">
        <SubSection title="Optimizing Committee Work">
          AI can generate meeting summaries, track action items, and provide background research for committee work, enabling professors to contribute more effectively while saving time.
        </SubSection>

        <SubSection title="Grant Writing Assistance">
          AI tools analyze successful grant proposals, suggest language and structural improvements, and ensure compliance with funding requirements, helping professors present their ideas more compellingly.
        </SubSection>

        <SubSection title="Facilitating Cross-Departmental Collaboration">
          AI can identify potential collaborators by analyzing research interests and publication records across campus, fostering interdisciplinary connections that might otherwise go unnoticed.
        </SubSection>
      </BlogSection>

      <BlogSection title="Thoughtful AI Integration in Academia" ismainsection="true">
        <SubSection title="Upholding Academic Integrity">
          Transparency is essential. Professors should clearly communicate how AI is used in their work, modeling responsible AI practices for students and maintaining trust in academic processes.
        </SubSection>

        <SubSection title="Preserving Human Connection">
          While AI handles routine tasks, the core of academia remains human: mentorship, collaboration, and intellectual discovery. AI should enhance—not replace—these vital relationships.
        </SubSection>

        <SubSection title="Ongoing Professional Development">
          Effective AI adoption requires investment in training and support. Institutions should provide resources to help professors understand AI's capabilities, limitations, and ethical considerations.
        </SubSection>
      </BlogSection>

      <BlogSection title="Embracing the Future" ismainsection="true">
        <ContentText>
          As AI reshapes higher education, the key question is not whether change will happen, but how thoughtfully it will be guided. For professors ready to embrace these innovations, AI offers the chance to elevate their impact—enabling them to teach, research, and mentor more effectively than ever.
        </ContentText>

        <ContentText>
          The classroom of the future will always be about human connection and intellectual growth. With AI as a partner, professors can devote more time and energy to what they do best: inspiring students and shaping the leaders of tomorrow.
        </ContentText>
      </BlogSection>
    </ContentContainer>
  );
};

const BlogSection = ({ title, children, ismainsection = "false" }) => {
  return (
    <SectionContainer>
      {title && (
        <SectionTitle ismainsection={ismainsection}>
          {title}
        </SectionTitle>
      )}
      <SectionContent>
        {children}
      </SectionContent>
    </SectionContainer>
  );
};

const SubSection = ({ title, children, image, imageAlt }) => {
  return (
    <SubSectionContainer>
      {image && (
        <SubSectionImage src={image} alt={imageAlt} />
      )}
      {title && (
        <SubSectionTitle>{title}</SubSectionTitle>
      )}
      <SubSectionText>{children}</SubSectionText>
    </SubSectionContainer>
  );
};

const SubSectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const SubSectionImage = styled.img`
  width: 763px;
  height: 400px;
  border-radius: 16px;
  max-width: 100%;
  object-fit: cover;
  @media (max-width: 991px) {
    width: 100%;
    height: 300px;
  }
  @media (max-width: 640px) {
    height: 240px;
  }
`;

const SubSectionTitle = styled.h3`
  color: #64636a;
  letter-spacing: -0.2px;
  font: 500 20px/140% Inter, sans-serif;
  margin: 0;
  @media (max-width: 640px) {
    font-size: 18px;
  }
`;

const SubSectionText = styled.p`
  align-self: stretch;
  color: #64636a;
  letter-spacing: -0.08px;
  font: 500 16px/160% Inter, sans-serif;
  margin: 0;
`;


const SectionContainer = styled.section`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  align-self: stretch;
`;

const SectionTitle = styled.h2`
  align-self: stretch;
  color: ${props => props.ismainsection ? '#1c1b1f' : '#64636a'};
  letter-spacing: ${props => props.ismainsection ? '-0.32px' : '-0.2px'};
  font: ${props => props.ismainsection ? '700 32px/120% Inter, sans-serif' : '500 20px/140% Inter, sans-serif'};
  margin: 0;
  @media (max-width: 640px) {
    font-size: ${props => props.ismainsection ? '24px' : '18px'};
  }
`;

const SectionContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 42px;
  align-self: stretch;
`;

const ContentText = styled.p`
  align-self: stretch;
  color: #64636a;
  letter-spacing: -0.08px;
  font: 500 16px/160% Inter, sans-serif;
  margin: 0;
`;


const HeaderContainer = styled.header`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 32px;
  align-self: stretch;
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
`;

const BackButtonWrapper = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 16px;
`;

const MainTitle = styled.h1`
  width: 100%;
  color: #1c1b1f;
  letter-spacing: -0.48px;
 
  font: 700 48px/120% Inter, sans-serif;
  margin: 0;
  @media (max-width: 991px) {
    font-size: 36px;
    line-height: 120%;
  }
  @media (max-width: 640px) {
    font-size: 28px;
    line-height: 120%;
  }
`;

const MetadataContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 16px;
`;

const MetadataWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1 0 0;
`;

const MetadataContent = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const DateText = styled.time`
  color: #64636a;
  letter-spacing: 2px;
  text-transform: uppercase;
  font: 400 16px/120% Inter, sans-serif;
  @media (max-width: 640px) {
    font-size: 14px;
  }
`;

const AuthorText = styled.span`
  color: #64636a;
  letter-spacing: 2px;
  text-transform: uppercase;
  font: 500 16px/120% Inter, sans-serif;
  @media (max-width: 640px) {
    font-size: 14px;
  }
`;

const HeroImage = styled.img`
  display: flex;
  width: 100%;
  height: 346px;
  justify-content: flex-end;
  align-items: center;
  border-radius: 16px;
  object-fit: cover;
  @media (max-width: 991px) {
    height: 280px;
  }
  @media (max-width: 640px) {
    height: 200px;
  }
`;

const IntroText = styled.p`
  align-self: stretch;
  color: #64636a;
  letter-spacing: -0.08px;
  font: 500 16px/160% Inter, sans-serif;
  margin: 0;
`;


const BlogContainer = styled.article`
  display: flex;
  width: 1120px;
  padding: 56px;
  flex-direction: column;
  align-items: flex-start;
  gap: 64px;
  border-radius: 24px;
  margin: 0 auto;
  max-width: 100%;
  box-sizing: border-box;
  background-color: #fff;
  @media (max-width: 991px) {
    width: 100%;
    padding: 40px 32px;
    gap: 48px;
  }
  @media (max-width: 640px) {
    padding: 24px 16px;
    gap: 32px;
  }
`;

const BlogWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 42px;
  align-self: stretch;
`;

const PageBackground = styled.div`
  height: 100%;
  width: 100%;
  background: linear-gradient(to bottom, #5e2bff 0px, #5e2bff 500px, #F5F5F9 100px, #F5F5F9 100%);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 200px 0;
    flex-direction: column;

  @media (max-width: 640px) {
    padding: 40px 0;
    background: linear-gradient(to bottom, #5e2bff 0px, #5e2bff 100px, #ffffff 100px, #ffffff 100%);
  }
`;

const Fullpage = styled.div`
  height: 100%;
  width: 100%;
//   background-color: #e20707ff;
  display: flex;
  justify-content: center;
  align-items: flex-start;
 

  @media (max-width: 640px) {
    padding: 40px 0;
   
  }
`;